import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import {
  InvocationStatusEnum,
  ProvisionMode,
  type CompanyShellRow,
  type CompanyTemplateRef,
  type MyPayEnvironment,
} from "../../generated/core-api";
import type { CompanyShellValidateResult } from "../types";
import { isTerminalInvocationStatus, useInvocationStatusPoll } from "./useInvocationStatusPoll";

/**
 * Runtime type-guard for the `company_shell_provision` workflow's own
 * `mode: "validate"` result shape — `AgentInvocationStatus.result` is typed
 * `unknown | null` in the generated client since this shape isn't
 * formalized in OpenAPI (see Pacific.Agentic.Api#834).
 */
export function isCompanyShellValidateResult(
  value: unknown
): value is CompanyShellValidateResult {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.row_count !== "number") return false;
  if (!Array.isArray(candidate.errors)) return false;
  const errorsOk = candidate.errors.every(
    (e) =>
      typeof e === "object" &&
      e !== null &&
      typeof (e as Record<string, unknown>).row_index === "number" &&
      typeof (e as Record<string, unknown>).message === "string" &&
      typeof (e as Record<string, unknown>).severity === "string"
  );
  if (!errorsOk) return false;
  // `normalisations` is optional/additive (Pacific.Agentic.Api#954): accept a
  // result that omits it entirely, but when present it must be an array of
  // well-shaped entries — don't let a malformed field slip through untyped.
  if (candidate.normalisations !== undefined) {
    if (!Array.isArray(candidate.normalisations)) return false;
    const normalisationsOk = candidate.normalisations.every(
      (n) =>
        typeof n === "object" &&
        n !== null &&
        typeof (n as Record<string, unknown>).row_index === "number" &&
        typeof (n as Record<string, unknown>).column === "string" &&
        typeof (n as Record<string, unknown>).normalised_value === "string"
    );
    if (!normalisationsOk) return false;
  }
  return true;
}

export interface InvokeCompanyShellProvisionArgs {
  agentId: string;
  serviceAccountId: string;
  template: CompanyTemplateRef;
  rows: CompanyShellRow[];
  mypayEnvironment: MyPayEnvironment;
  correlationId?: string;
}

export interface CompanyShellProvisionInvocationState {
  invoke: (args: InvokeCompanyShellProvisionArgs) => void;
  workflowId: string | null;
  status: InvocationStatusEnum | null;
  /** Parsed result once `status === "COMPLETED"`; null otherwise, including when `result` didn't match the expected shape. */
  result: CompanyShellValidateResult | null;
  /** The invocation-start call failing, or `AgentInvocationStatus.error` once `status === "FAILED"`. */
  error: string | null;
  isInvoking: boolean;
  isPolling: boolean;
  reset: () => void;
}

/**
 * Invoke the `company_shell_provision` agent workflow in the given `mode`
 * and poll its status until terminal, using the generated
 * `api.invokeAgent`/`api.getInvocationStatus` directly (these endpoints are
 * already in the OpenAPI spec, unlike most of shadow-runs' hand-rolled-axios
 * endpoints — see `src/AGENTS.md`'s "prefer generated clients" rule).
 */
function useCompanyShellProvisionInvocation(
  mode: ProvisionMode
): CompanyShellProvisionInvocationState {
  const [agentId, setAgentId] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);

  const invokeMutation = useMutation({
    mutationFn: (args: InvokeCompanyShellProvisionArgs) =>
      api.invokeAgent(args.agentId, {
        service_account_id: args.serviceAccountId,
        input_payload: {
          workflow: "company_shell_provision",
          mode,
          template: args.template,
          rows: args.rows,
          mypay_environment: args.mypayEnvironment,
          ...(args.correlationId
            ? { correlation_id: args.correlationId }
            : {}),
        },
      }),
    onSuccess: (data, variables) => {
      setAgentId(variables.agentId);
      setWorkflowId(data.workflow_id);
    },
  });

  const statusQuery = useInvocationStatusPoll(agentId, workflowId);

  const status = statusQuery.data?.status ?? null;
  const result =
    status === InvocationStatusEnum.COMPLETED &&
    isCompanyShellValidateResult(statusQuery.data?.result)
      ? statusQuery.data.result
      : null;

  const invoke = useCallback((args: InvokeCompanyShellProvisionArgs) => {
    setWorkflowId(null);
    invokeMutation.mutate(args);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    setAgentId(null);
    setWorkflowId(null);
    invokeMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    invoke,
    workflowId,
    status,
    result,
    error: invokeMutation.error?.message ?? statusQuery.data?.error ?? null,
    isInvoking: invokeMutation.isPending,
    isPolling: !!workflowId && !isTerminalInvocationStatus(status),
    reset,
  };
}

export function useValidateCompanyShellProvision(): CompanyShellProvisionInvocationState {
  return useCompanyShellProvisionInvocation(ProvisionMode.validate);
}

export function useCommitCompanyShellProvision(): CompanyShellProvisionInvocationState {
  return useCompanyShellProvisionInvocation(ProvisionMode.commit);
}
